import {
	Caption,
	Heading,
	Icon,
	Modal,
	Spinner,
	Tooltip,
} from "@shopify/polaris"
import { ClipboardMinor } from "@shopify/polaris-icons"
import React, { useEffect, useState } from "react"
import ChromeWebStoreLogo from "../assets/chrome-web-store.png"
import kcrxLinking1 from "../assets/kcrx-linking-1.jpg"
import kcrxLinking2 from "../assets/kcrx-linking-2.jpg"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch.js"
import kopyLogo from "../assets/kopy-logo-square.png"
import { useTranslation } from "react-i18next"
import { Video } from "./Video.jsx"

export const CRXSettingsModal = ({ open, setOpen }) => {
	const { t } = useTranslation()
	const authenticatedFetch = useAuthenticatedFetch()

	const [loadingLinkingCode, setLoadingLinkingCode] = useState(false)
	const [linkingCode, setLinkingCode] = useState(null)
	const [copiedToClipboard, setCopiedToClipboard] = useState(false)

	const copyLinkingCodeToClipboard = () => {
		setCopiedToClipboard(true)
		navigator?.clipboard?.writeText(linkingCode)

		setTimeout(() => {
			setCopiedToClipboard(false)
		}, 1000)
	}

	useEffect(async () => {
		if (!open) {
			setLinkingCode(null)
		}

		if (open && !loadingLinkingCode) {
			setLoadingLinkingCode(true)

			await authenticatedFetch("/api/crx/linkingCode", {
				method: "GET",
			})
				.then((res) => res.json())
				.then((data) => {
					setLinkingCode(data?.linkingCode || null)
				})
				.catch((err) => {
					console.error(err)
				})

			setLoadingLinkingCode(false)
		}
	}, [open])

	return (
		<Modal
			title={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.5rem",
					}}
				>
					<img
						style={{
							width: "1.875rem",
							height: "1.875rem",
							borderRadius: "0.3125rem",
						}}
						src={kopyLogo}
					/>
					<span>Kopy Chrome Extension</span>
				</div>
			}
			open={open}
			onClose={() => setOpen(false)}
		>
			<Modal.Section>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "1.5rem",
					}}
				>
					<div>
						<Heading>{t("kopy_crx.step_1")}</Heading>
						<Caption>{t("kopy_crx.step_1_1")}</Caption>
						<div
							style={{
								marginTop: "1rem",
								display: "flex",
								justifyContent: "center",
							}}
						>
							<a
								target="_blank"
								rel="noreferrer"
								href="https://chrome.google.com/webstore/detail/kopy-browser-extension/bdgjkkdfafbckdafophnhkhldcdbijfp"
							>
								<img
									style={{
										height: "5rem",
										border: "1px solid #eee",
										borderRadius: "0.625rem",
									}}
									src={ChromeWebStoreLogo}
								/>
							</a>
						</div>
					</div>

					<div>
						<Heading>{t("kopy_crx.step_2")}</Heading>
						<Caption>{t("kopy_crx.step_2_1")}</Caption>

						<div
							style={{
								marginTop: "1rem",
								display: "flex",
								justifyContent: "center",
							}}
						>
							{loadingLinkingCode ? (
								<Spinner />
							) : linkingCode ? (
								<div
									style={{
										display: "flex",
										border: "1px solid #ddd",
										borderRadius: "0.625rem",
									}}
								>
									<code
										style={{
											fontSize: "1.25rem",
											padding: "0.5rem",
										}}
									>
										{linkingCode}
									</code>
									<div
										style={{
											borderLeft: "1px solid #ddd",
											padding: "0.5rem",
											cursor: "pointer",
											display: "flex",
											alignItems: "center",
										}}
										onClick={copyLinkingCodeToClipboard}
									>
										<Icon
											color={
												copiedToClipboard
													? "primary"
													: "subdued"
											}
											source={ClipboardMinor}
										/>
									</div>
								</div>
							) : (
								<div style={{ color: "#cb1919" }}>
									{t(
										"kopy_crx.error_no_linking_code_obtained",
									)}
								</div>
							)}
						</div>
					</div>

					<div>
						<Heading>{t("kopy_crx.step_3")}</Heading>
						<Caption>{t("kopy_crx.step_3_1")}</Caption>
						<div
							style={{
								marginTop: "1rem",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								gap: "1rem",
							}}
						>
							<img
								style={{
									height: "11.25rem",
									border: "1px solid #eee",
									borderRadius: "0.625rem",
								}}
								src={kcrxLinking1}
							/>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="1rem"
								height="1rem"
								viewBox="0 0 24 24"
							>
								<path
									fill="currentColor"
									d="M12.7 17.925q-.35.2-.625-.063T12 17.25L14.425 13H3q-.425 0-.713-.288T2 12q0-.425.288-.713T3 11h11.425L12 6.75q-.2-.35.075-.613t.625-.062l7.975 5.075q.475.3.475.85t-.475.85L12.7 17.925Z"
								></path>
							</svg>
							<img
								style={{
									height: "11.25rem",
									border: "1px solid #eee",
									borderRadius: "0.625rem",
								}}
								src={kcrxLinking2}
							/>
						</div>
					</div>

					<div>
						<Heading>{t("kopy_crx.step_4")}</Heading>
						<Caption>{t("kopy_crx.step_4_1")}</Caption>
						<div style={{ marginTop: "1rem" }}>
							<Video videoSrc="https://kopy.b-cdn.net/crx_import.webm" />
						</div>
					</div>
				</div>
			</Modal.Section>
		</Modal>
	)
}

const CRXSettings = () => {
	const [open, setOpen] = useState(false)
	const { t } = useTranslation()

	return (
		<Tooltip
			content={
				<div style={{ padding: "0.2rem 0.4rem" }}>
					<span style={{ fontSize: "0.8rem", color: "#222" }}>
						{t("header.chrome_extension_tooltip")}
					</span>
				</div>
			}
		>
			<div
				id="crx-button"
				style={{
					background: "#e7e7e7",
					borderRadius: "100%",
					padding: "0.4rem",
					position: "relative",
				}}
			>
				<div
					onClick={() => setOpen(true)}
					style={{
						cursor: "pointer",
						verticalAlign: "middle",
						width: "1.25rem",
						height: "1.25rem",
					}}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="1.25rem"
						height="1.25rem"
						viewBox="0 0 256 256"
					>
						<path
							fill="#FFF"
							d="M128.003 199.216c39.335 0 71.221-31.888 71.221-71.223c0-39.335-31.886-71.223-71.221-71.223c-39.335 0-71.222 31.888-71.222 71.223c0 39.335 31.887 71.223 71.222 71.223Z"
						></path>
						<path
							fill="#229342"
							d="M35.89 92.997c-5.313-9.203-11.558-18.862-18.736-28.977a127.98 127.98 0 0 0 110.857 191.981c11.78-16.523 19.78-28.437 23.996-35.74c8.099-14.028 18.573-34.112 31.423-60.251v-.015a63.993 63.993 0 0 1-110.857.017c-17.453-32.548-29.68-54.887-36.683-67.015Z"
						></path>
						<path
							fill="#FBC116"
							d="M128.008 255.996A127.972 127.972 0 0 0 256 127.997A127.983 127.983 0 0 0 238.837 64c-24.248-2.39-42.143-3.585-53.686-3.585c-13.088 0-32.139 1.195-57.152 3.585l-.014.01a63.993 63.993 0 0 1 55.444 31.987a63.993 63.993 0 0 1-.001 64.01l-55.42 95.989Z"
						></path>
						<path
							fill="#1A73E8"
							d="M128.003 178.677c27.984 0 50.669-22.685 50.669-50.67c0-27.986-22.685-50.67-50.67-50.67c-27.983 0-50.669 22.686-50.669 50.67s22.686 50.67 50.67 50.67Z"
						></path>
						<path
							fill="#E33B2E"
							d="M128.003 64.004H238.84a127.973 127.973 0 0 0-221.685.015l55.419 95.99l.015.008a63.993 63.993 0 0 1 55.415-96.014l-.002.001Z"
						></path>
					</svg>
				</div>

				<CRXSettingsModal open={open} setOpen={setOpen} />
			</div>
		</Tooltip>
	)
}

export default CRXSettings
